import React, {useMemo} from 'react'
import { Button } from 'antd'
import moment from 'moment'
import { IBestDataset } from '../../../../../../types/analytics'
import { IEnrichedMetadata } from '../../../../../../types/metadata'
import { IAdvancedAnalyticsApiPayload } from '../../../../../../types/chart'
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput'
import { MarkerItem } from '../../../../../../types/home'
import { useHistogramParams } from "../../../hooks/useHistogramParams"
import { AnalyticsSection } from "../../Layout/AnalyticsSection"
import HistogramInputForm from "./HistogramInputForm"
import HistogramChart from "./HistogramChart"
import HistogramAdditionalInput from "./HistogramAdditionalInput"

interface HistogramProps {
  coordinates: number[]
  previewDatasetId: string
  datasets: IBestDataset[]
  dateRange: [moment.Moment, moment.Moment]
  enrichedMetadata: IEnrichedMetadata[]
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void
  histogramImage: string
  histogramLinks: string[]
  error: string
  updating: boolean
  pointName: string
  userInput: IAnalytcisUserInput
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  onDatasetChange: (item: string) => void
  point?: MarkerItem
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
  isOfflineDataset: boolean
  gridCellSize: string,
  waterDepth: string,
}

export default function Histogram(props: HistogramProps) {
  const {
    datasets,
    histogramImage,
    histogramLinks,
    error,
    updating,
    pointName,
    isOfflineDataset
  } = props

  const {
    dataset,
    variablesX,
    variablesY,
    variableX,
    variableY,
    month,
    startDate,
    endDate,
    categories,
    onUpdateHistogram,
    onSelectDataset,
    doVariableXChange,
    doVariableYChange,
    doMonthChange,
    doStartDateChange,
    doEndDateChange,
    doDirectionalDatasetChange,
    directionalDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
    directionalInterval,
    doDirectionalIntervalChange,
    magnitudeInterval,
    doMagnitudeIntervalChange
  } = useHistogramParams(props)

  // TODO: enable cache 
  // const upsertHistogramCache = useAnalyticsCacheStore(state => state.upsertHistogramCache)

  // useEffect(() => {
  //   if (!props.coordinates || !props.previewDatasetId || !variableX?.Index) return;

  //   const key = `${props.previewDatasetId}_${variableX.Index}_${props.coordinates[0]},${props.coordinates[1]}`;
  //   upsertHistogramCache(key, {
  //     variableXIndex: variableX.Index,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     histogramImage,
  //     histogramLinks
  //   });

  // }, [upsertHistogramCache, props.coordinates, props.previewDatasetId, variableX?.Index, startDate, endDate, props.isForAllAnalytics, histogramLinks]);

  const plotInputContent = useMemo(() => (
    <HistogramInputForm
      datasets={datasets}
      dataset={props.previewDatasetId}
      onSelectDataset={onSelectDataset}
      categories={categories}
      variablesX={variablesX}
      variableX={variableX}
      onVariableXChange={doVariableXChange}
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={doStartDateChange}
      onEndDateChange={doEndDateChange}
      disabledStartDate={disabledStartDate}
      disabledEndDate={disabledEndDate}
      isForAllAnalytics={props.isForAllAnalytics}
      onCheckboxChange={handleCheckboxChange}
      isLoading={updating}
      isOfflineDataset={isOfflineDataset}

    />
  ), [
    datasets,
    dataset,
    onSelectDataset,
    categories,
    variablesX,
    variableX,
    doVariableXChange,
    startDate,
    endDate,
    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    props.isForAllAnalytics,
    handleCheckboxChange,
    updating,
    error,
    directionalInterval,
    doDirectionalIntervalChange,
    magnitudeInterval,
    doMagnitudeIntervalChange
  ])

  const updateContent = useMemo(() => (
    <div style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '36px'
    }}>
      <Button
        type="primary"
        id="Analytics-Histogram-Update_Button"
        disabled={updating || isOfflineDataset}
        loading={updating}
        onClick={() => onUpdateHistogram()}
        style={{
          width: '90px',
          height: '36px'
        }}
      >
        Update
      </Button>
    </div>
  ), [
    updating,
    onUpdateHistogram
  ])

  const chartContent = useMemo(() => (
    <HistogramChart
      histogramImage={histogramImage}
      histogramLinks={histogramLinks}
      pointName={pointName}
      isLoading={updating}
      error={error}
      isOfflineDataset={isOfflineDataset}
    />
  ), [
    histogramImage,
    histogramLinks,
    pointName,
    updating,
    error
  ])

  const additionalContent = useMemo(() => (
    <HistogramAdditionalInput
      variablesY={variablesY}
      variableY={variableY}
      onVariableYChange={doVariableYChange}
      month={month}
      onMonthChange={doMonthChange}
      isLoading={updating}
      datasets={datasets}
      categories={categories}
      onDirectionalDatasetChange={doDirectionalDatasetChange}
      directionalDataset={directionalDataset}
      directionalInterval={directionalInterval}
      onDirectionalIntervalChange={doDirectionalIntervalChange}
      magnitudeInterval={magnitudeInterval}
      onMagnitudeIntervalChange={doMagnitudeIntervalChange}
    />
  ), [
    variablesY,
    variableY,
    doVariableYChange,
    directionalDataset,
    directionalInterval,
    magnitudeInterval,
    month,
    doMonthChange,
    updating,
    error
  ])

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={additionalContent}
      isLoading={updating}
    />
  )
}
